import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4005/graphql').replace('/graphql', '');

function resolveUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

/**
 * Carga una imagen como base64 para incrustar en el PDF
 */
async function loadImageBase64(url) {
  const fullUrl = resolveUrl(url);
  if (!fullUrl) return null;
  try {
    const response = await fetch(fullUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Genera un PDF de factura local a partir de datos de FacturaCompleta
 * @param {Object} factura - Datos completos de la factura (de obtenerFacturaCompleta)
 * @param {string} modo - 'ver' para abrir en nueva pestaña, 'descargar' para download
 */
export async function generarFacturaPDF(factura, modo = 'ver') {
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 15;

  const emisor = factura.emisor || {};
  const cliente = factura.cliente || {};
  const resolucion = factura.resolucion || null;
  const detalles = Array.isArray(factura.detalles) ? factura.detalles : [];
  const metodosPago = Array.isArray(factura.metodos_pago) ? factura.metodos_pago : [];

  const fmt = (n) => {
    const val = parseFloat(n) || 0;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(val);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleDateString('es-CO', {
        timeZone: 'America/Bogota',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return fecha;
    }
  };

  // ─── ENCABEZADO: LOGO + DATOS DEL HOTEL ───

  // Logo del hotel
  if (emisor.logo_url) {
    const logoData = await loadImageBase64(emisor.logo_url);
    if (logoData) {
      const logoWidth = 30;
      const logoHeight = 30;
      const logoX = (pageWidth - logoWidth) / 2;
      doc.addImage(logoData, 'PNG', logoX, y, logoWidth, logoHeight);
      y += logoHeight + 3;
    }
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(emisor.nombre || 'Hotel', pageWidth / 2, y, { align: 'center' });
  y += 5;

  // Eslogan del hotel
  if (emisor.eslogan) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(emisor.eslogan, pageWidth / 2, y, { align: 'center' });
    y += 4;
  }

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (emisor.nit) {
    doc.text(`NIT: ${emisor.nit}`, pageWidth / 2, y, { align: 'center' });
    y += 4;
  }
  if (emisor.direccion) {
    const dirLine = [emisor.direccion, emisor.ciudad, emisor.departamento].filter(Boolean).join(', ');
    doc.text(dirLine, pageWidth / 2, y, { align: 'center' });
    y += 4;
  }
  if (emisor.telefono) {
    doc.text(`Tel: ${emisor.telefono}`, pageWidth / 2, y, { align: 'center' });
    y += 4;
  }
  if (emisor.email) {
    doc.text(emisor.email, pageWidth / 2, y, { align: 'center' });
    y += 4;
  }

  // Línea separadora
  y += 2;
  doc.setDrawColor(100);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;

  // ─── NÚMERO DE FACTURA ───
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `FACTURA DE VENTA No. ${factura.numero_display || ((factura.prefijo || '') + (factura.numero_factura || ''))}`,
    pageWidth / 2,
    y,
    { align: 'center' }
  );
  y += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Fecha: ${formatFecha(factura.fecha)}`, pageWidth / 2, y, { align: 'center' });
  y += 4;

  if (factura.tipo_factura) {
    const tipo = factura.tipo_factura === 'checkout' ? 'Hospedaje' : factura.tipo_factura === 'venta_pos' ? 'Venta POS' : factura.tipo_factura;
    doc.text(`Tipo: ${tipo}`, pageWidth / 2, y, { align: 'center' });
    y += 4;
  }

  // Línea separadora
  doc.setDrawColor(180);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;

  // ─── DATOS DEL CLIENTE ───
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENTE:', margin, y);
  doc.setFont('helvetica', 'normal');
  y += 4;

  if (cliente.nombre) {
    doc.text(`Nombre: ${cliente.nombre}`, margin, y);
    y += 4;
  }
  if (cliente.tipo_documento && cliente.numero_documento) {
    doc.text(`${cliente.tipo_documento}: ${cliente.numero_documento}`, margin, y);
    y += 4;
  }
  if (cliente.direccion) {
    doc.text(`Dirección: ${cliente.direccion}`, margin, y);
    y += 4;
  }
  if (cliente.telefono) {
    doc.text(`Teléfono: ${cliente.telefono}`, margin, y);
    y += 4;
  }

  if (!cliente.nombre) {
    doc.text('Consumidor Final', margin, y);
    y += 4;
  }

  y += 3;

  // ─── TABLA DE ITEMS ───
  if (detalles.length > 0) {
    const tableBody = detalles.map((item) => [
      item.descripcion || 'Item',
      (item.cantidad || 1).toString(),
      fmt(item.precio_unitario || 0),
      `${item.iva_porcentaje || 0}%`,
      fmt(item.total || 0),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Descripción', 'Cant.', 'Precio Unit.', 'IVA', 'Total']],
      body: tableBody,
      theme: 'striped',
      headStyles: {
        fillColor: [55, 65, 81],
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 18 },
        2: { halign: 'right', cellWidth: 30 },
        3: { halign: 'center', cellWidth: 18 },
        4: { halign: 'right', cellWidth: 30 },
      },
      margin: { left: margin, right: margin },
    });

    y = doc.lastAutoTable.finalY + 5;
  }

  // ─── TOTALES ───
  const totalesX = pageWidth - margin - 60;
  const valoresX = pageWidth - margin;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  doc.text('Subtotal:', totalesX, y, { align: 'right' });
  doc.text(fmt(factura.subtotal), valoresX, y, { align: 'right' });
  y += 4;

  if (factura.descuento > 0) {
    doc.text('Descuento:', totalesX, y, { align: 'right' });
    doc.text(`-${fmt(factura.descuento)}`, valoresX, y, { align: 'right' });
    y += 4;
  }

  if (factura.iva > 0) {
    doc.text('IVA:', totalesX, y, { align: 'right' });
    doc.text(fmt(factura.iva), valoresX, y, { align: 'right' });
    y += 4;
  }

  if (factura.propina > 0) {
    doc.text('Propina:', totalesX, y, { align: 'right' });
    doc.text(fmt(factura.propina), valoresX, y, { align: 'right' });
    y += 4;
  }

  // Total en negrita
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL:', totalesX, y, { align: 'right' });
  doc.text(fmt(factura.total), valoresX, y, { align: 'right' });
  y += 7;

  // ─── MÉTODOS DE PAGO ───
  if (metodosPago.length > 0) {
    doc.setDrawColor(180);
    doc.line(margin, y, pageWidth - margin, y);
    y += 4;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('FORMA DE PAGO:', margin, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    metodosPago.forEach((mp) => {
      const ref = mp.referencia ? ` (Ref: ${mp.referencia})` : '';
      doc.text(`${mp.metodo || mp.nombre || 'Pago'}: ${fmt(mp.monto || mp.valor || 0)}${ref}`, margin + 5, y);
      y += 4;
    });
  }

  // ─── OBSERVACIONES ───
  if (factura.observaciones) {
    y += 3;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(`Observaciones: ${factura.observaciones}`, margin, y, { maxWidth: pageWidth - margin * 2 });
    y += 6;
  }

  // ─── PIE DE PÁGINA ───
  y += 5;
  doc.setDrawColor(180);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Gracias por su preferencia', pageWidth / 2, y, { align: 'center' });

  // ─── GENERAR ───
  const filename = `Factura_${factura.numero_display || factura.numero_factura || factura.id}.pdf`;

  if (modo === 'descargar') {
    doc.save(filename);
  } else {
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }
}
